import React, { useState } from "react";
import { Box, Container, Dialog, Paper } from "@mui/material";
import { useDispatch, useSelector } from "../../../store";
import DeleteConfirmation from "../../base/DeleteConfirmation";
import { disableAlert, fetchAlerts } from "../../../slices/alert";
import ActiveAlertsTable from "./ActiveAlertsTable";
import {
  NotificationsActive,
  Campaign,
  TrackChanges,
} from "@mui/icons-material";
import InfoBox from "../../base/InfoBox";
import InfoBoxGroup from "../../base/InfoBoxGroup";
import ActiveAlertsStack from "./ActiveAlertsStack";
import checkRole, { UserRoles } from "../../../helpers/checkRole";

interface Props {}

const ActiveAlertsView: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  const { loggedInUser } = useSelector((store) => store.auth);
  const { status } = useSelector((store) => store.alert);

  const [modalOpen, setModalOpen] = useState<"edit" | "disable" | "none">(
    "none",
  );
  const [selectedAlert, setSelectedAlert] = useState<number | undefined>();

  const handleView = (id: number) => {
    window.open(`tile/${id}`, "_blank");
  };

  const handleEdit = (id: number) => {
    window.open(`edit-tile/${id}`, "_blank");
  };

  const handleOpenDisable = (id: number) => {
    setSelectedAlert(id);
    setModalOpen("disable");
  };

  const handleClose = () => {
    setModalOpen("none");
    dispatch(fetchAlerts());
    setSelectedAlert(undefined);
  };

  const handleDisable = async () => {
    await dispatch(disableAlert(selectedAlert!));
    handleClose();
  };

  const infoBoxes = [
    {
      text: "The table below displays all tiles currently in alert status.",
      icon: <NotificationsActive fontSize="inherit" color="warning" />,
    },
    {
      text: "Click the buttons in the Action column to modify or disable alerts for a tile.",
      icon: <TrackChanges fontSize="inherit" color="inherit" />,
    },
    {
      text: "Visit the Notification Setup page to configure how alerts are sent.",
      icon: <Campaign fontSize="inherit" color="success" />,
    },
  ];

  return (
    <Container sx={{ marginBottom: 8 }} disableGutters>
      <InfoBoxGroup
        content={infoBoxes.map((info, index) => {
          return <InfoBox key={index} text={info.text} icon={info.icon} />;
        })}
      />
      <Paper
        sx={{
          paddingY: 3,
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        <Box
          display={{ xs: "none", md: "flex" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ maxWidth: "100%" }}
        >
          <ActiveAlertsTable
            handleView={handleView}
            handleEdit={
              checkRole(loggedInUser!, UserRoles.AnyAdmin)
                ? handleEdit
                : undefined
            }
            handleDisable={
              checkRole(loggedInUser!, UserRoles.AnyAdmin)
                ? handleOpenDisable
                : undefined
            }
          />
        </Box>
        <Box
          display={{ xs: "flex", md: "none" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ width: "100%" }}
        >
          <ActiveAlertsStack
            handleView={handleView}
            handleEdit={
              checkRole(loggedInUser!, UserRoles.AnyAdmin)
                ? handleEdit
                : undefined
            }
            handleDisable={
              checkRole(loggedInUser!, UserRoles.AnyAdmin)
                ? handleOpenDisable
                : undefined
            }
          />
        </Box>
      </Paper>
      <Dialog open={modalOpen === "disable"}>
        <DeleteConfirmation
          name="this alert"
          extraInfo="This will stop all future alerts on this tile. Return to tile edit page to add new alerts."
          handleClose={handleClose}
          handleDelete={handleDisable}
          loading={status === "loading"}
        />
      </Dialog>
    </Container>
  );
};

export default ActiveAlertsView;
